import { FinancingApi, Provider } from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import {
    SetupDataStore,
    useProviderConfig,
    TurnsServiceDegradedStore,
    useTurnsIntegration,
} from '@servicetitan/financing-workflow-shared';
import { SelectField } from '@servicetitan/anvil2-react-hook-form';
import { CSSProperties, useMemo } from 'react';

interface IndustryTypeComboboxProps {
    fieldPrefix?: string;
    style?: CSSProperties;
}

export const IndustryTypeCombobox = ({ fieldPrefix = '', style }: IndustryTypeComboboxProps) => {
    const [apiClient] = useDependencies(FinancingApi);
    const { setupData } = SetupDataStore.useStore();
    const { setIsTurnsServiceDegraded } = TurnsServiceDegradedStore.useStore();
    const { additionalApplicantFields = [] } = useProviderConfig();
    const turnsIntegration = useTurnsIntegration();

    const fieldName = `${fieldPrefix}.industryType`;

    const { data } = useQuery({
        queryKey: ['industryTypes'],
        queryFn: async () => {
            try {
                const response = await apiClient.providersGetCatalog(
                    turnsIntegration.isEnabled
                        ? Provider.Turns
                        : ((setupData?.provider as Provider) ?? Provider.None),
                    'Industries',
                );

                return response.data;
            } catch {
                setIsTurnsServiceDegraded(true);
                return { items: [] };
            }
        },
        enabled: additionalApplicantFields.includes('jobDetails') || turnsIntegration.isEnabled,
    });

    // Convert to SelectFieldOption format
    const options = useMemo(() => {
        const validItems =
            data?.items?.filter(item => item.key != null && item.displayName != null) ?? [];
        return validItems.map(item => ({
            id: item.key!,
            label: item.displayName!,
        }));
    }, [data?.items]);

    return data != null ? (
        <SelectField
            name={fieldName}
            options={options}
            label="Improvement Type"
            required
            style={style}
        />
    ) : null;
};
